import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { ShieldAlert, AlertTriangle, AlertCircle, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';

const SEV_CONFIG = {
  critical: {
    label_ar: 'حرجة',
    label_en: 'Critical',
    Icon: ShieldAlert,
    color: 'text-red-400',
    bg: 'bg-red-500/15 border-red-500/40',
    rowBg: 'bg-red-500/5',
  },
  high: {
    label_ar: 'عالية',
    label_en: 'High',
    Icon: AlertTriangle,
    color: 'text-orange-400',
    bg: 'bg-orange-500/15 border-orange-500/40',
    rowBg: 'bg-orange-500/5',
  },
  info: {
    label_ar: 'تنبيه',
    label_en: 'Info',
    Icon: AlertCircle,
    color: 'text-amber-400',
    bg: 'bg-amber-500/15 border-amber-500/40',
    rowBg: 'bg-amber-500/5',
  },
};

const STATUS_BADGES = {
  pending: { ar: 'قيد المراجعة', en: 'Pending', cls: 'bg-amber-500/20 text-amber-300 border-amber-500/40' },
  approved_by_admin: { ar: 'مشروع — بأمر المدير', en: 'Approved by admin', cls: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' },
  dismissed: { ar: 'تم الصرف', en: 'Dismissed', cls: 'bg-gray-500/20 text-gray-300 border-gray-500/40' },
};

const AbuseAttemptsTab = ({ isArabic }) => {
  const [data, setData] = useState({ items: [], by_barber: {}, total: 0 });
  const [filter, setFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);
  const [reviewing, setReviewing] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter !== 'all') params.append('severity', filter);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      params.append('days', String(days));
      const res = await api.get(`/loyalty/abuse-attempts?${params.toString()}`);
      setData(res.data || { items: [], by_barber: {}, total: 0 });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل تحميل السجل');
    } finally {
      setLoading(false);
    }
  }, [filter, statusFilter, days]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleReview = async (attemptId, action) => {
    const note = window.prompt(
      isArabic
        ? (action === 'approve' ? 'سبب الاعتماد (اختياري):' : 'سبب الصرف (اختياري):')
        : (action === 'approve' ? 'Approval reason (optional):' : 'Dismissal reason (optional):'),
      ''
    );
    if (note === null) return; // user cancelled
    setReviewing(attemptId);
    try {
      await api.post(`/loyalty/abuse-attempts/${attemptId}/${action}`, { note: note || null });
      toast.success(
        isArabic
          ? (action === 'approve' ? 'تم الاعتماد كمشروع' : 'تم الصرف')
          : (action === 'approve' ? 'Approved' : 'Dismissed')
      );
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || (isArabic ? 'فشل الإجراء' : 'Failed'));
    } finally {
      setReviewing(null);
    }
  };

  const maxByBarber = Math.max(
    1,
    ...Object.values(data.by_barber).map((v) => v.total || 0)
  );

  return (
    <Card className="overflow-hidden bg-card border-white/10" data-testid="abuse-attempts-panel">
      <div className="p-4 border-b border-white/10 flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-bold flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-red-400" />
            {isArabic ? 'سجل المحاولات المشبوهة' : 'Suspicious Attempts Log'}
          </h3>
          <p className="text-xs text-muted-foreground mt-1">
            {isArabic
              ? 'محاولات الحلاقين لربط إيرادات سابقة ببطاقات عمرها أكثر من 3 أيام. اعتمد المحاولة كمشروعة إذا كانت بأمر المدير.'
              : 'Barber attempts to attach past revenues to memberships older than 3 days. Approve as legitimate if by admin order.'}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-secondary/50 border border-white/10 rounded px-2 py-1 text-sm"
            data-testid="abuse-status-filter"
          >
            <option value="pending">{isArabic ? 'قيد المراجعة' : 'Pending'}</option>
            <option value="approved_by_admin">{isArabic ? 'معتمدة' : 'Approved'}</option>
            <option value="dismissed">{isArabic ? 'مصروفة' : 'Dismissed'}</option>
            <option value="all">{isArabic ? 'الكل' : 'All'}</option>
          </select>
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="bg-secondary/50 border border-white/10 rounded px-2 py-1 text-sm"
            data-testid="abuse-days-filter"
          >
            <option value={7}>{isArabic ? 'آخر 7 أيام' : 'Last 7 days'}</option>
            <option value={30}>{isArabic ? 'آخر 30 يوم' : 'Last 30 days'}</option>
            <option value={90}>{isArabic ? 'آخر 90 يوم' : 'Last 90 days'}</option>
          </select>
          {['all', 'critical', 'high', 'info'].map((f) => (
            <Button
              key={f}
              size="sm"
              variant={filter === f ? 'default' : 'outline'}
              onClick={() => setFilter(f)}
              data-testid={`abuse-filter-${f}`}
            >
              {f === 'all'
                ? isArabic
                  ? 'الكل'
                  : 'All'
                : isArabic
                ? SEV_CONFIG[f].label_ar
                : SEV_CONFIG[f].label_en}
            </Button>
          ))}
        </div>
      </div>

      {/* Per-barber chart */}
      {Object.keys(data.by_barber).length > 0 && (
        <div className="p-4 border-b border-white/10 space-y-2 bg-secondary/20">
          <h4 className="text-sm font-semibold text-muted-foreground mb-2">
            {isArabic ? 'توزيع المحاولات لكل حلاق' : 'Attempts by Barber'}
          </h4>
          {Object.entries(data.by_barber)
            .sort((a, b) => b[1].total - a[1].total)
            .map(([name, sev]) => (
              <div key={name} data-testid={`abuse-bar-${name}`}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="font-medium">{name}</span>
                  <span className="font-bold">
                    {sev.total}{' '}
                    <span className="text-muted-foreground font-normal">
                      ({sev.critical || 0} حرجة · {sev.high || 0} عالية · {sev.info || 0} تنبيه)
                    </span>
                  </span>
                </div>
                <div className="h-6 bg-secondary/50 rounded overflow-hidden flex">
                  {sev.critical > 0 && (
                    <div
                      className="bg-red-500 h-full"
                      style={{ width: `${(sev.critical / maxByBarber) * 100}%` }}
                      title={`${sev.critical} critical`}
                    />
                  )}
                  {sev.high > 0 && (
                    <div
                      className="bg-orange-500 h-full"
                      style={{ width: `${(sev.high / maxByBarber) * 100}%` }}
                      title={`${sev.high} high`}
                    />
                  )}
                  {sev.info > 0 && (
                    <div
                      className="bg-amber-500 h-full"
                      style={{ width: `${(sev.info / maxByBarber) * 100}%` }}
                      title={`${sev.info} info`}
                    />
                  )}
                </div>
              </div>
            ))}
        </div>
      )}

      {/* Detail table */}
      {loading ? (
        <div className="text-center py-10 text-muted-foreground">
          {isArabic ? 'جاري التحميل...' : 'Loading...'}
        </div>
      ) : data.items.length === 0 ? (
        <div className="text-center py-10 text-muted-foreground">
          {isArabic ? '✅ لا توجد محاولات مشبوهة' : '✅ No suspicious attempts'}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40">
              <tr>
                <th className="text-start py-3 px-3">{isArabic ? 'الشدة' : 'Severity'}</th>
                <th className="text-start py-3 px-3">{isArabic ? 'الحلاق' : 'Barber'}</th>
                <th className="text-start py-3 px-3">{isArabic ? 'العضوية' : 'Membership'}</th>
                <th className="text-center py-3 px-3">{isArabic ? 'عمر البطاقة' : 'Card Age'}</th>
                <th className="text-center py-3 px-3">{isArabic ? 'الإيراد' : 'Revenue'}</th>
                <th className="text-start py-3 px-3">{isArabic ? 'التاريخ' : 'Date'}</th>
                <th className="text-start py-3 px-3">{isArabic ? 'الحالة' : 'Status'}</th>
                <th className="text-end py-3 px-3">{isArabic ? 'إجراء' : 'Action'}</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((it) => {
                const cfg = SEV_CONFIG[it.severity] || SEV_CONFIG.info;
                const { Icon } = cfg;
                const status = it.status || 'pending';
                const sb = STATUS_BADGES[status] || STATUS_BADGES.pending;
                const isReviewing = reviewing === it.id;
                return (
                  <tr key={it.id} className={`border-t border-white/5 ${cfg.rowBg}`} data-testid={`abuse-row-${it.id}`}>
                    <td className="py-3 px-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border ${cfg.bg} ${cfg.color} text-[10px] font-bold`}>
                        <Icon className="w-3 h-3" />
                        {isArabic ? cfg.label_ar : cfg.label_en}
                      </span>
                    </td>
                    <td className="py-3 px-3 font-medium">{it.barber_name || '?'}</td>
                    <td className="py-3 px-3">
                      <div className="font-mono">#{it.membership_number}</div>
                      <div className="text-[10px] text-muted-foreground">{it.membership_name}</div>
                    </td>
                    <td className="py-3 px-3 text-center font-bold">
                      <span className={cfg.color}>{it.membership_age_days} {isArabic ? 'يوم' : 'd'}</span>
                    </td>
                    <td className="py-3 px-3 text-center">
                      {Number(it.revenue_amount || 0).toFixed(2)} د.إ
                    </td>
                    <td className="py-3 px-3 text-xs text-muted-foreground">
                      {new Date(it.created_at).toLocaleString(isArabic ? 'ar-AE' : 'en-GB')}
                    </td>
                    <td className="py-3 px-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-bold ${sb.cls}`}>
                        {isArabic ? sb.ar : sb.en}
                      </span>
                      {it.review_by_name && (
                        <div className="text-[10px] text-muted-foreground mt-0.5">
                          {isArabic ? 'بواسطة' : 'by'} {it.review_by_name}
                        </div>
                      )}
                      {it.review_note && (
                        <div className="text-[10px] text-muted-foreground italic mt-0.5">"{it.review_note}"</div>
                      )}
                    </td>
                    <td className="py-3 px-3 text-end">
                      {status === 'pending' ? (
                        <div className="flex gap-1 justify-end">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-emerald-300 hover:text-emerald-200 border-emerald-500/40"
                            disabled={isReviewing}
                            onClick={() => handleReview(it.id, 'approve')}
                            data-testid={`abuse-approve-${it.id}`}
                          >
                            {isReviewing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                            <span className="ms-1">{isArabic ? 'اعتماد' : 'Approve'}</span>
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-gray-300 hover:text-gray-200"
                            disabled={isReviewing}
                            onClick={() => handleReview(it.id, 'dismiss')}
                            data-testid={`abuse-dismiss-${it.id}`}
                          >
                            <XCircle className="w-3.5 h-3.5" />
                            <span className="ms-1">{isArabic ? 'صرف' : 'Dismiss'}</span>
                          </Button>
                        </div>
                      ) : (
                        <span className="text-[10px] text-muted-foreground">
                          {it.review_at && new Date(it.review_at).toLocaleDateString(isArabic ? 'ar-AE' : 'en-GB')}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};

export default AbuseAttemptsTab;
